import React from "react";
import Modal from "react-modal";
import { Field, Form, Formik } from "formik";
import { Oval } from "react-loader-spinner";
import * as Yup from "yup";
import toast from "react-hot-toast";

import { useAppSelector, useAppDispatch } from "../../../../app/hooks";
import {
  toggleShowAddDumpsterModal,
  createDumpster,
} from "../../dumpstersSlice";
import { ReactComponent as Close } from "../../../../assets/images/close.svg";
import { RadioButton } from "../../../Shared";
import { DumpsterSize, DumpsterFillStatus } from "../../../../models";

import "./AddDumpsterModal.scss";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#FFFFFF",
  },
};

const validationSchema = Yup.object().shape({
  label: Yup.string().required("Label is required"),
  dumpsterSize: Yup.string().required("Dumpster size is required"),
});

export interface AddDumpsterFormModel {
  label: string;
  dumpsterSize: DumpsterSize;
  notes?: string;
  fillStatus: DumpsterFillStatus;
}

const initialValues: AddDumpsterFormModel = {
  label: "",
  dumpsterSize: DumpsterSize.SIZE_10,
  notes: "",
  fillStatus: "empty",
};

export function AddDumpsterModal() {
  const dispatch = useAppDispatch();

  const isOpen = useAppSelector(
    (state) => state.dumpsters.showAddDumpsterModal
  );
  const addDumpsterAPI = useAppSelector(
    (state) => state.dumpsters.createDumpster
  );

  React.useEffect(() => {
    if (!addDumpsterAPI.loading && !!addDumpsterAPI.error) {
      toast.error("Something went wrong. Please try again.");
    }

    if (
      !addDumpsterAPI.loading &&
      !addDumpsterAPI.error &&
      addDumpsterAPI.value
    ) {
      toast.success("Successfully added dumpster.");
    }
  }, [
    addDumpsterAPI.loading,
    addDumpsterAPI.error,
    addDumpsterAPI.value,
    dispatch,
  ]);

  const toggleModal = () => {
    dispatch(toggleShowAddDumpsterModal());
  };

  const onSubmit = (form: AddDumpsterFormModel) => {
    dispatch(createDumpster(form));
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={toggleModal} style={modalStyles}>
      <div className="add-dumpster-modal-component">
        <Close className="close-button" onClick={toggleModal} />

        <div className="title">Add Dumpster</div>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={validationSchema}
        >
          {({ errors, touched }) => (
            <Form className="add-dumpster-form">
              <div className="field-container">
                <div className="label">Label:</div>
                <Field id="label" name="label" className="text-input" />
                {errors.label && touched.label && (
                  <div className="form-error">{errors.label}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Dumpster Size:</div>
                <div className="radio-button-options-container">
                  <RadioButton
                    name="dumpsterSize"
                    value={DumpsterSize.SIZE_10}
                    label="10 yards"
                  />
                  <RadioButton
                    name="dumpsterSize"
                    value={DumpsterSize.SIZE_15}
                    label="15 yards"
                  />
                  <RadioButton
                    name="dumpsterSize"
                    value={DumpsterSize.SIZE_CLEAN_FILL}
                    label="Clean Fill"
                  />
                </div>
                {errors.dumpsterSize && touched.dumpsterSize && (
                  <div className="form-error">{errors.dumpsterSize}</div>
                )}
              </div>

              <div className="field-container">
                <div className="label">Fill Status:</div>
                <div className="radio-button-options-container">
                  <RadioButton name="fillStatus" value="empty" label="Empty" />
                  <RadioButton name="fillStatus" value="half" label="Half" />
                  <RadioButton name="fillStatus" value="full" label="Full" />
                </div>
              </div>

              <div className="field-container">
                <div className="label">Notes:</div>
                <Field
                  id="notes"
                  name="notes"
                  as="textarea"
                  className="text-input"
                  rows={3}
                />
              </div>

              <button
                type="submit"
                className="add-button"
                disabled={addDumpsterAPI.loading}
              >
                Add Dumpster
                {addDumpsterAPI.loading && (
                  <div style={{ marginLeft: 8 }}>
                    <Oval
                      color="#131313"
                      height={20}
                      width={20}
                      secondaryColor="#FBD20A"
                    />
                  </div>
                )}
              </button>

              {!!addDumpsterAPI.error && (
                <div className="error-text">
                  Something went wrong. Please try again.
                </div>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  );
}
